import React, {useMemo, useState} from 'react';
import {useApolloClient} from '@apollo/client';
import {
  Anchor,
  Button,
  Checkbox,
  Dialogue,
  Icon,
  Pagination,
  SearchInput,
  Text,
  WithTooltip,
  addToast
} from '@albert-io/atomic';

import {normalizeSearchString} from 'lib/stringUtils';

import type {AdminSchool} from './useAdminSchools';
import {computeLockChange, computeUnlockChange} from './contentLockDerivation';
import type {GuideTree} from './contentLockDerivation';
import {AdminSchoolContentLocksDocument} from './adminContentLocks.queries.generated';
import {
  useLockContentMutation,
  useUnlockContentMutation
} from './contentLockMutations.queries.generated';
import {
  GUIDE_LEVEL_LOCK_TYPE,
  restrictionRemovalMessage
} from './manageRestrictionsModal.constants';
import {
  formatRemovalDate,
  getRestrictionRemovalDate,
  isInRestrictionGraceWindow
} from './proExpiry';
import {SchoolRestrictionRow} from './SchoolRestrictionRow';

/** Rows per page in the school table (Figma table spec: four rows, paginated). */
const SCHOOLS_PER_PAGE = 4;

const LEARN_MORE_URL =
  'https://help.albert.io/en/articles/15879223-how-do-i-restrict-assessment-content-for-my-school';

interface RestrictionFormProps {
  guideLevelName: string;
  guideLevelId?: string;
  guideLevelTree?: GuideTree;
  schools: AdminSchool[];
  /** Ids of schools that already restrict this guide level — the initial toggle state. */
  initialRestrictedIds: string[];
  getLockedSourceIds: (schoolId: string) => Set<string>;
  getLockId: (schoolId: string, sourceId: string) => string | undefined;
  onClose: () => void;
}

/**
 * Grace-window membership + display order for the school table. One derivation feeds both the
 * header note and the warnings-first sort: membership keys on the INITIAL restriction state, which
 * is also the live state for exactly these rows — a grace school's toggle is locked, so the
 * pending set can never diverge from the seed for them.
 *
 * Order: schools with removal warnings first, alphabetical within each group (Figma table spec);
 * the search filters by school name without disturbing that order.
 */
function useSchoolOrdering(
  schools: AdminSchool[],
  initialRestrictedIds: ReadonlySet<string>,
  searchTerm: string
): {graceWindowSchools: AdminSchool[]; sortedSchools: AdminSchool[]} {
  const graceWindowSchools = useMemo(
    () =>
      schools.filter((school) =>
        isInRestrictionGraceWindow(school, initialRestrictedIds.has(school.id))
      ),
    [schools, initialRestrictedIds]
  );

  const sortedSchools = useMemo(() => {
    const graceWindowIds = new Set(graceWindowSchools.map(({id}) => id));
    const normalizedTerm = normalizeSearchString(searchTerm);
    return [...schools]
      .filter(
        (school) => !normalizedTerm || normalizeSearchString(school.name).includes(normalizedTerm)
      )
      .sort(
        (a, b) =>
          Number(graceWindowIds.has(b.id)) - Number(graceWindowIds.has(a.id)) ||
          a.name.localeCompare(b.name)
      );
  }, [schools, graceWindowSchools, searchTerm]);

  return {graceWindowSchools, sortedSchools};
}

export const RestrictionForm = ({
  guideLevelName,
  guideLevelId,
  guideLevelTree,
  schools,
  initialRestrictedIds,
  getLockedSourceIds,
  getLockId,
  onClose
}: RestrictionFormProps) => {
  const [restrictedSchoolIds, setRestrictedSchoolIds] = useState<Set<string>>(
    () => new Set(initialRestrictedIds)
  );
  // Baseline seed state, diffed against the edited set on confirm.
  const [initialIds] = useState<Set<string>>(() => new Set(initialRestrictedIds));
  const [acknowledged, setAcknowledged] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);

  const [lockContent] = useLockContentMutation();
  const [unlockContent] = useUnlockContentMutation();
  const client = useApolloClient();

  const {graceWindowSchools, sortedSchools} = useSchoolOrdering(schools, initialIds, searchTerm);

  const totalPages = Math.max(1, Math.ceil(sortedSchools.length / SCHOOLS_PER_PAGE));
  // Clamp instead of resetting in an effect: shrinking the result set (searching) can leave the
  // selected page past the end.
  const currentPage = Math.min(page, totalPages);
  const pagedSchools = sortedSchools.slice(
    (currentPage - 1) * SCHOOLS_PER_PAGE,
    currentPage * SCHOOLS_PER_PAGE
  );

  const toggleSchool = (schoolId: string) => {
    setRestrictedSchoolIds((prev) => {
      const next = new Set(prev);
      if (next.has(schoolId)) {
        next.delete(schoolId);
      } else {
        next.add(schoolId);
      }
      return next;
    });
  };

  const handleConfirm = async () => {
    const changedSchools = schools.filter(
      (school) => restrictedSchoolIds.has(school.id) !== initialIds.has(school.id)
    );

    if (!guideLevelId || changedSchools.length === 0) {
      onClose();
      return;
    }

    setSaving(true);

    try {
      await Promise.all(
        changedSchools.map(async (school) => {
          const lockedSourceIds = getLockedSourceIds(school.id);
          const wantsRestricted = restrictedSchoolIds.has(school.id);

          let change: {toLock: string[]; toUnlock: string[]};
          if (guideLevelTree) {
            change = wantsRestricted
              ? computeLockChange(guideLevelTree, guideLevelId, lockedSourceIds)
              : computeUnlockChange(guideLevelTree, guideLevelId, lockedSourceIds);
          } else {
            change = wantsRestricted
              ? {toLock: [guideLevelId], toUnlock: []}
              : {toLock: [], toUnlock: lockedSourceIds.has(guideLevelId) ? [guideLevelId] : []};
          }

          await Promise.all([
            ...change.toLock.map(async (sourceId) => {
              const {data} = await lockContent({
                variables: {
                  input: {lockType: GUIDE_LEVEL_LOCK_TYPE, sourceId, schoolId: school.id}
                }
              });
              // Ash mutations can resolve at the transport layer yet carry policy/validation errors.
              if (data?.lockContent?.errors?.length) {
                throw new Error(
                  data.lockContent.errors[0]?.message ?? 'Failed to restrict content'
                );
              }
            }),
            ...change.toUnlock.map(async (sourceId) => {
              const lockId = getLockId(school.id, sourceId);
              if (!lockId) {
                return;
              }
              const {data} = await unlockContent({variables: {id: lockId}});
              if (data?.unlockContent?.errors?.length) {
                throw new Error(
                  data.unlockContent.errors[0]?.message ?? 'Failed to unrestrict content'
                );
              }
            })
          ]);
        })
      );

      // Refresh each changed school's locks once (not once per mutation) so the useAdminContentLocks
      // watchers re-render the live indicators. A failed refresh must not fail an already-saved change.
      try {
        await Promise.all(
          changedSchools.map((school) =>
            client.query({
              query: AdminSchoolContentLocksDocument,
              variables: {schoolId: school.id},
              fetchPolicy: 'network-only'
            })
          )
        );
      } catch {
        // Best-effort refresh; the save itself succeeded.
      }

      addToast({
        color: 'positive',
        title: 'Restrictions updated',
        message: `Restrictions for ${guideLevelName} have been saved.`
      });
      onClose();
    } catch {
      addToast({
        color: 'negative',
        title: "Couldn't update restrictions",
        message: 'Something went wrong saving your changes. Please try again.'
      });
      setSaving(false);
    }
  };

  return (
    <>
      <Dialogue.Body>
        <div className='gap-x1 flex items-center'>
          <Text as='div'>{guideLevelName}</Text>
          <WithTooltip
            theme='light'
            placement='bottom-start'
            focusable
            interactive
            content={
              <Text size='s'>
                Restricting blocks teachers and students at that school from seeing the question
                content outside of assignments you authorize.{' '}
                <Anchor href={LEARN_MORE_URL} target='_blank' rel='noopener noreferrer' underlined>
                  Learn more
                </Anchor>
              </Text>
            }
          >
            <span data-testid='manage-restrictions__guide-info' className='flex'>
              <Icon icon='info-circle' iconStyle='regular' aria-hidden />
            </span>
          </WithTooltip>
        </div>

        {graceWindowSchools.length > 0 && (
          <div className='mt-x1 gap-x1 flex items-center' data-testid='restrictions-removal-note'>
            <Text color='warning'>
              {`${graceWindowSchools.length} of your schools ${
                graceWindowSchools.length === 1 ? 'has' : 'have'
              } restrictions that will be removed soon.`}
            </Text>
            <WithTooltip
              theme='light'
              placement='bottom-start'
              focusable
              content={
                <div className='flex flex-col text-left'>
                  {graceWindowSchools.map((school) => {
                    const removalDate = getRestrictionRemovalDate(school);
                    return (
                      <div key={school.id} className='px-x1-half py-x1'>
                        <Text as='div' size='s'>
                          {school.name}
                        </Text>
                        {removalDate && (
                          <Text as='div' size='xs' color='warning'>
                            <Icon
                              icon='exclamation-triangle'
                              iconStyle='regular'
                              color='warning'
                              aria-hidden
                              className='mr-x0-half'
                            />
                            {restrictionRemovalMessage(formatRemovalDate(removalDate))}
                          </Text>
                        )}
                      </div>
                    );
                  })}
                </div>
              }
            >
              <span data-testid='restrictions-removal-note__info' className='flex'>
                <Icon icon='info-circle' iconStyle='regular' color='warning' aria-hidden />
              </span>
            </WithTooltip>
          </div>
        )}

        <SearchInput
          rectangle
          className='mt-x4 max-w-[256px]'
          aria-label='Search schools'
          placeholder='Search'
          value={searchTerm}
          data-testid='manage-restrictions__school-search'
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setSearchTerm(event.target.value)
          }
          onReset={() => setSearchTerm('')}
        />

        <div className='mt-x1 rounded-x1 border-border-primary border'>
          <table className='w-full border-collapse'>
            <thead>
              <tr className='background-primary sticky top-0 z-10'>
                <th className='border-border-primary px-x2 py-x1-half w-full border-b text-left'>
                  <Text bold size='s'>
                    School
                  </Text>
                </th>
                <th className='border-border-primary px-x2 py-x1-half border-b border-l text-right whitespace-nowrap'>
                  <Text bold size='s'>
                    Restrict
                  </Text>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedSchools.length === 0 && (
                <tr>
                  <td colSpan={2} className='px-x2 py-x1-half'>
                    <Text color='tertiary'>No schools match your search.</Text>
                  </td>
                </tr>
              )}
              {pagedSchools.map((school) => (
                <SchoolRestrictionRow
                  key={school.id}
                  school={school}
                  guideLevelId={guideLevelId}
                  isRestricted={restrictedSchoolIds.has(school.id)}
                  // A running grace row locks the school outright, even while the stored
                  // license still reads active (the row is the backend's lapse signal).
                  canAdjust={school.hasPro && !school.restrictionRemovalDate}
                  onToggle={() => toggleSchool(school.id)}
                />
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className='mt-x1 flex justify-end'>
            <Pagination
              key={searchTerm}
              align='right'
              variation='condensed'
              totalPages={totalPages}
              defaultPage={1}
              onChange={setPage}
            />
          </div>
        )}

        <label className='mt-x4 gap-x1-half flex cursor-pointer items-start'>
          <Checkbox
            className='mt-x0-quarter shrink-0'
            checked={acknowledged}
            onChange={(event) => setAcknowledged(event.target.checked)}
            aria-hidden={false}
            label={`I understand that all questions in “${guideLevelName}” and any questions in this guide will be restricted.`}
            size='s'
          />
          <Text size='s'>
            {`I understand that all questions in “${guideLevelName}” and any questions in this guide will be restricted.`}
          </Text>
        </label>
      </Dialogue.Body>

      <Button
        className='mt-x4 w-full'
        color='primary'
        disabled={!acknowledged || saving}
        onClick={handleConfirm}
      >
        {saving ? 'Saving…' : 'Confirm'}
      </Button>
    </>
  );
};

export default RestrictionForm;
